import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Radio, MapPin, Navigation } from "lucide-react";
import { apiBaseUrl, authHeaders, toUtcDate } from "../pages/dispatcher/shared";
import { TILE_LAYER_ATTRIBUTION, useTileLayerUrl } from "../mapTiles";

// Live "who's idle-moving right now" view. Auto-refreshes every REFRESH_MS.
// Freshness is bounded by the telematics sync (~a few minutes), NOT real-time.

const REFRESH_MS = 60000;
const WINDOW_MIN = 20;   // "active" = idle breadcrumb within the last N minutes

function liveTruckIcon(label, stale) {
  const c = stale ? "#94a3b8" : "#d97706";
  const html = `
    <div style="display:flex;flex-direction:column;align-items:center;pointer-events:none;">
      <div style="background:${c};color:#fff;border:1px solid #0b1020;font-size:11px;font-weight:800;
        padding:2px 8px;border-radius:999px;white-space:nowrap;margin-bottom:2px;box-shadow:0 2px 8px rgba(0,0,0,.5);">${label || "truck"}</div>
      <svg width="42" height="42" viewBox="0 0 26 26" style="filter:drop-shadow(0 2px 5px rgba(0,0,0,.5));">
        <rect x="1" y="9" width="14" height="8" rx="1.5" fill="${c}" stroke="#0b1020" stroke-width="1.2"/>
        <path d="M15 11 h6 l3 4 v2 h-9 z" fill="${c}" stroke="#0b1020" stroke-width="1.2"/>
        <circle cx="6" cy="18" r="2.3" fill="#0b0d12" stroke="#e5e7eb" stroke-width="0.8"/>
        <circle cx="20" cy="18" r="2.3" fill="#0b0d12" stroke="#e5e7eb" stroke-width="0.8"/>
      </svg>
    </div>`;
  return L.divIcon({ html, className: "", iconSize: [96, 64], iconAnchor: [48, 52] });
}

function Fit({ points }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !points || points.length === 0) return;
    done.current = true;
    if (points.length === 1) map.setView(points[0], 9);
    else map.fitBounds(L.latLngBounds(points), { padding: [50, 50], maxZoom: 9 });
  }, [points, map]);
  return null;
}

const compass = (d) => (d == null ? "—" : ["N", "NE", "E", "SE", "S", "SW", "W", "NW"][Math.round(d / 45) % 8]);
const miles = (n) => `${Math.round(Number(n || 0)).toLocaleString("en-US")} mi`;
function ago(iso) {
  const s = (Date.now() - toUtcDate(iso).getTime()) / 1000;
  if (s < 90) return "just now";
  const m = Math.round(s / 60);
  return m < 60 ? `${m}m ago` : `${Math.round(m / 60)}h ago`;
}

export default function ActiveNowIdle({ setRoute, scope }) {
  const tileUrl = useTileLayerUrl();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () => {
      axios
        .get(`${apiBaseUrl}/fleet/idle-movement/active-now`, { ...authHeaders(), params: { minutes: WINDOW_MIN, scope } })
        .then((res) => { if (alive) { setData(res.data); setError(null); } })
        .catch((err) => { if (alive) setError(err.response?.data?.detail || err.message); })
        .finally(() => { if (alive) setLoaded(true); });
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(id); };
  }, [scope]);

  const trucks = data?.trucks || [];
  const points = trucks.filter((t) => t.current?.lat != null).map((t) => [t.current.lat, t.current.lng]);

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <div style={S.title}>
          <Radio size={15} color="#d97706" /> Active Now
          <span style={S.live}>live</span>
        </div>
        <div style={S.sub}>
          {loaded ? `${trucks.length} truck${trucks.length === 1 ? "" : "s"} idle-moving in the last ${WINDOW_MIN} min` : "Loading…"}
          {data?.as_of && <span style={{ marginLeft: 8, color: "var(--muted)" }}>· updated {toUtcDate(data.as_of).toLocaleTimeString()}</span>}
          <span style={{ marginLeft: 8, color: "var(--muted)" }}>· refreshes every 60s</span>
        </div>
      </div>

      {error && <div style={S.err}>Couldn't load live view: {error}</div>}

      {loaded && trucks.length === 0 && !error && (
        <div style={S.empty}>No trucks are idle-moving right now — everyone is loaded, deadheading, or parked.</div>
      )}

      {trucks.length > 0 && (
        <>
          <div style={S.mapBox}>
            <MapContainer center={points[0] || [39.5, -98.35]} zoom={6} style={{ height: "100%", width: "100%" }}>
              {tileUrl && <TileLayer attribution={TILE_LAYER_ATTRIBUTION} url={tileUrl} />}
              <Fit points={points} />
              {trucks.filter((t) => t.current?.lat != null).map((t) => (
                <Marker key={t.truck_id} position={[t.current.lat, t.current.lng]}
                  icon={liveTruckIcon(t.unit_number, false)}
                  eventHandlers={{ click: () => openRoute(t, setRoute) }}>
                  <Tooltip>{t.driver_name} · {miles(t.ongoing_idle_miles)} idle · {ago(t.current.recorded_at)}</Tooltip>
                </Marker>
              ))}
            </MapContainer>
          </div>

          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Driver</th>
                  <th style={S.th}>Truck</th>
                  <th style={S.thR}>Idle now</th>
                  <th style={S.th}>Heading</th>
                  <th style={S.th}>Came from</th>
                  <th style={S.th}>Next booked</th>
                  <th style={S.th}>Last seen</th>
                  <th style={S.th} />
                </tr>
              </thead>
              <tbody>
                {trucks.map((t) => (
                  <tr key={t.truck_id} style={S.tr}>
                    <td style={{ ...S.td, fontWeight: 700, color: t.driver_id ? "var(--ink)" : "#c07f1f" }}>{t.driver_name}</td>
                    <td style={S.td}>#{t.unit_number || "?"}</td>
                    <td style={{ ...S.tdR, fontWeight: 800, color: "#c07f1f" }}>{miles(t.ongoing_idle_miles)}</td>
                    <td style={S.td}><Navigation size={12} style={{ transform: `rotate(${t.heading_deg || 0}deg)`, marginRight: 4, color: "var(--muted)" }} />{compass(t.heading_deg)}</td>
                    <td style={S.td}>{t.came_from ? (t.came_from.destination || "load") : <span style={S.dim}>—</span>}</td>
                    <td style={S.td}>
                      {t.next_booked_load
                        ? (t.next_booked_load.origin || "booked load")
                        : <span style={S.noDest}>No destination</span>}
                    </td>
                    <td style={S.td}>{ago(t.current.recorded_at)}</td>
                    <td style={S.td}>
                      <button style={S.viewBtn} onClick={() => openRoute(t, setRoute)}><MapPin size={12} /> View Route</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function openRoute(t, setRoute) {
  const lane = `${t.came_from ? (t.came_from.destination || "load") : "no prev"} → ${t.next_booked_load ? (t.next_booked_load.origin || "booked") : "no destination"}`;
  setRoute({ segId: t.seg_id, unitNumber: t.unit_number, driverName: t.driver_name, laneLabel: lane });
}

const S = {
  wrap: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: 14, marginBottom: 20, boxShadow: "var(--shadow-xs)" },
  head: { display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 12 },
  title: { display: "flex", alignItems: "center", gap: 7, fontSize: 15, fontWeight: 800, color: "var(--ink)" },
  live: { fontSize: 10, fontWeight: 800, letterSpacing: ".05em", textTransform: "uppercase", color: "#fff", background: "#d97706", borderRadius: 999, padding: "2px 7px" },
  sub: { fontSize: 12, color: "var(--ink-2)" },
  err: { background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626", borderRadius: 8, padding: "10px 14px", fontSize: 12.5 },
  empty: { fontSize: 12.5, color: "var(--muted)", padding: "10px 2px" },
  mapBox: { height: 340, borderRadius: 10, overflow: "hidden", border: "1px solid var(--line)", marginBottom: 12 },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12.5 },
  th: { textAlign: "left", padding: "8px 8px", color: "var(--muted)", fontWeight: 600, borderBottom: "1px solid var(--line)" },
  thR: { textAlign: "right", padding: "8px 8px", color: "var(--muted)", fontWeight: 600, borderBottom: "1px solid var(--line)" },
  tr: { borderBottom: "1px solid var(--line)" },
  td: { padding: "8px 8px", color: "var(--ink-2)", verticalAlign: "middle" },
  tdR: { padding: "8px 8px", color: "var(--ink-2)", textAlign: "right" },
  dim: { color: "var(--muted)" },
  noDest: { color: "#c07f1f", fontWeight: 700 },
  viewBtn: { display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600, color: "var(--brand, #2563eb)", background: "var(--card)", border: "1px solid var(--brand, #2563eb)", borderRadius: 7, padding: "5px 10px", cursor: "pointer", whiteSpace: "nowrap" },
};
